import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { PipelineStep } from '../../const/PipelineTypes';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { MultiPanelTile, Panel, PanelAction, Tooltip, Tag, Tabs, Tab } from '@dsi/react-eds';
import styles from './PipelineDetail.module.css';
import { useTheme } from '../../hooks/useTheme';
import StagesView from './stages/StagesView';
import StepsLogView from './log-viewer/StepsLogView';
import StagesSideMenu from './stages/StagesSideMenu';
import { useFetchLogs } from './log-viewer/useFetchLogs';
import { useFetchPipeline } from './useFetchPipeline';
import NodeActivityStatus from './nodes/NodeActivityStatus';
import NodeActivitySummary from './nodes/NodeActivitySummary';
import GnbDeployNodes from './GnbDeployNodes';

const PipelineDetail: React.FC = () => {
  const location = useLocation();
  const params = useParams<{ ticketId: string; automationFlowId: string }>();
  const { theme } = useTheme();

  const { pipeline, isPipelineLoading, refetch } = useFetchPipeline(location.state?.pipeline, params.ticketId, params.automationFlowId);
  const expandStage = location.state?.expandStage;
  const expandStep = location.state?.expandStep;
  const logsUrl = location.state?.logsUrl;
  const initialTab = location.state?.initialTab;

  const [selectedTab, setSelectedTab] = useState(initialTab === 'steps' ? 1 : initialTab === 'deployment' ? 2 : 0);
  const [leftPanelOpen, setLeftPanelOpen] = useState(initialTab === 'steps');
  const [selectedStage, setSelectedStage] = useState<string>(expandStage || '');
  const [selectedStep, setSelectedStep] = useState<string>(expandStep || '');
  const [currentLogsUrl, setCurrentLogsUrl] = useState<string>(logsUrl || '');
  const { logs, isLoading, error } = useFetchLogs(currentLogsUrl);
  const [expandedStages, setExpandedStages] = useState<Record<string, boolean>>(expandStage ? { [expandStage]: true } : {});

  const handleTabChange = (_: React.SyntheticEvent, activeTab: number) => {
    setSelectedTab(activeTab);
  };

  // Reset states on pipeline change (unless navigated from step click)
  useEffect(() => {
    if (!initialTab) {
      // For a submitted CR gNB ticket, a cold load (e.g. manual refresh after submit) should
      // land on the Node Management tab where the deployment status view lives — not the empty
      // default tab, which felt like the page "no longer existed".
      const isSubmittedCloudRan =
        pipeline?.metadata?.ProductLine?.toLowerCase().includes('cloud ran') &&
        (pipeline as { gnbDeploySubmitted?: boolean })?.gnbDeploySubmitted;
      setSelectedTab(isSubmittedCloudRan ? 2 : 0);
      setLeftPanelOpen(false);
      setSelectedStage('');
      setSelectedStep('');
      setCurrentLogsUrl('');
      setExpandedStages({});
    }
  }, [pipeline?.TicketId, initialTab]);

  // Group steps by stage

  const stages = useMemo(() => {
    return (
      pipeline?.steps?.reduce(
        (acc: Record<string, PipelineStep[]>, step: PipelineStep) => {
          if (!acc[step.Stage]) acc[step.Stage] = [];
          acc[step.Stage].push(step);
          return acc;
        },
        {} as Record<string, PipelineStep[]>
      ) || {}
    );
  }, [pipeline?.steps]);

  // Open left panel and select first step when switching to Steps tab
  useEffect(() => {
    if (selectedTab === 1) {
      setLeftPanelOpen(true);
      if (!selectedStep && pipeline?.steps?.length && pipeline?.steps?.length > 0) {
        const firstStage = Object.keys(stages)[0];
        const firstStep = stages[firstStage]?.[0];
        if (firstStage && firstStep) {
          setSelectedStage(firstStage);
          setSelectedStep(firstStep.Step);
          setCurrentLogsUrl(firstStep.logs || '');
          setExpandedStages({ [firstStage]: true });
        }
      } else if (selectedStage) {
        setExpandedStages({ [selectedStage]: true });
      }
    } else {
      setLeftPanelOpen(false);
    }
  }, [selectedTab, pipeline?.steps?.length, selectedStep, selectedStage, stages]);

  // Sync logs URL when step selection changes
  useEffect(() => {
    if (selectedStage && selectedStep && stages[selectedStage]) {
      const stepObj = stages[selectedStage].find((s: PipelineStep) => s.Step === selectedStep);
      if (stepObj?.logs !== currentLogsUrl) {
        setCurrentLogsUrl(stepObj?.logs || '');
      }
    }
  }, [selectedStage, selectedStep, stages, currentLogsUrl]);

  // Handle initial expand stage/step from navigation
  useEffect(() => {
    if (expandStage) {
      setExpandedStages((prev) => ({ ...prev, [expandStage]: true }));
      if (expandStage !== selectedStage) setSelectedStage(expandStage);
    }
    if (expandStep && expandStep !== selectedStep) setSelectedStep(expandStep);
  }, [expandStage, expandStep]);

  // Trigger resize for LogViewer dimension recalculation
  useEffect(() => {
    const timer = setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
    return () => clearTimeout(timer);
  }, [leftPanelOpen]);

  useEffect(() => {
    const handleNavToggle = () => setTimeout(() => window.dispatchEvent(new Event('resize')), 300);
    const navToggleButton = document.querySelector('[data-testid="navigation-toggle"], .eds-navigation-toggle, .navigation-toggle');
    if (navToggleButton) {
      navToggleButton.addEventListener('click', handleNavToggle);
      return () => navToggleButton.removeEventListener('click', handleNavToggle);
    }
    const handleClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.closest('[role="button"]') && target.textContent?.includes('☰')) handleNavToggle();
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  if (isPipelineLoading) return <div style={{ padding: 24 }} />;
  if (!pipeline) return <div style={{ padding: 24 }}>Pipeline not found</div>;

  const predefinedStages: string[] = pipeline.metadata?.predefinedStages || [];
  const clusterName = pipeline.automationFlow?.eiapCluster?.name || 'CSP Lab';
  const environmentGroups: Record<string, string[]> = { [clusterName]: predefinedStages };

  const isStepCompletedButStageRunning = (() => {
    if (!selectedStage || !selectedStep) return false;
    const steps = stages[selectedStage];
    if (!steps) return false;
    const stepObj = steps.find((s: PipelineStep) => s.Step === selectedStep);
    const stageStatus = steps.length > 0 ? steps[steps.length - 1].Status : undefined;
    return stepObj?.Status === 'success' && stageStatus === 'in-progress';
  })();

  const handleStepClick = (stageName: string, step: PipelineStep) => {
    setSelectedTab(1);
    setSelectedStage(stageName);
    setSelectedStep(step.Step);
    setCurrentLogsUrl(step.logs || '');
    setExpandedStages({ [stageName]: true });
  };

  const handleToggleStage = (stageName: string) => {
    setExpandedStages((prev) => ({ ...prev, [stageName]: !prev[stageName] }));
  };

  return (
    <ReactEDSWrapper
      component={MultiPanelTile}
      style={{ minHeight: '100%', overflowY: 'clip' }}
      title={`${pipeline.automationFlow.name} - ${pipeline.automationFlow.cardName}`}
      leftPanelAction={
        <ReactEDSWrapper component={Tooltip} message="Toggle stages/steps menu" pointer>
          <ReactEDSWrapper
            component={PanelAction}
            icon={leftPanelOpen ? 'sidemenu-left-close' : 'sidemenu-left-open'}
            onClick={() => setLeftPanelOpen(!leftPanelOpen)}
          />
        </ReactEDSWrapper>
      }
      leftPanel={
        <ReactEDSWrapper component={Panel} title={`Ticket: ${pipeline.TicketId}`} onClose={() => setLeftPanelOpen(false)}>
          <StagesSideMenu
            environmentGroups={environmentGroups}
            pipeline={pipeline}
            stages={stages}
            expandedStages={expandedStages}
            selectedStage={selectedStage}
            selectedStep={selectedStep}
            theme={theme}
            onToggleStage={handleToggleStage}
            onStepClick={handleStepClick}
          />
        </ReactEDSWrapper>
      }
      leftPanelOpen={leftPanelOpen}
    >
      <div className={styles.tagsRow}>
        {pipeline.metadata.ProductLine && (
          <ReactEDSWrapper component={Tag} color="blue">
            {pipeline.metadata.ProductLine}
          </ReactEDSWrapper>
        )}
        {pipeline.metadata.Header && (
          <ReactEDSWrapper component={Tag} color="purple">
            {pipeline.metadata.Header}
          </ReactEDSWrapper>
        )}
        {Array.isArray(pipeline.metadata.Products) &&
          pipeline.metadata.Products.map((product: { ProductNumber: string; RState?: string }, idx: number) => (
            <ReactEDSWrapper key={idx} component={Tag} color="green">
              {`${product.ProductNumber} (${product.RState})`}
            </ReactEDSWrapper>
          ))}
      </div>

      <Tabs activeTab={selectedTab} onTabChange={handleTabChange}>
        <Tab title="Stages">
          {selectedTab === 0 && (
            <StagesView
              predefinedStages={predefinedStages}
              pipeline={pipeline}
              stages={stages}
              expandedStages={expandedStages}
              theme={theme}
              onToggleStage={handleToggleStage}
              onStepClick={handleStepClick}
            />
          )}
        </Tab>
        <Tab title="Steps">
          {selectedTab === 1 && (
            <StepsLogView
              logs={logs}
              isLoading={isLoading}
              error={error}
              isStepCompletedButStageRunning={isStepCompletedButStageRunning}
              leftPanelOpen={leftPanelOpen}
              theme={theme}
            />
          )}
        </Tab>
        {
          pipeline?.metadata?.ProductLine?.toLowerCase().includes('cloud ran') ? (
            <Tab title="Node Management">
              {selectedTab === 2 && (
                <>
                  <GnbDeployNodes pipeline={pipeline} onSubmitted={refetch} />
                </>
              )}
            </Tab>
          ) : (
            (null as unknown as React.ReactElement)
          ) /* EDS Tabs requires ReactElement children */
        }
        {
          pipeline?.metadata?.ProductLine?.toLowerCase().includes('baseband radio node') ? (
            <Tab title="Deployment Summary">
              {selectedTab === 2 && (
                <>
                  <NodeActivitySummary workflowUuid={pipeline.id} />
                  <NodeActivityStatus workflowUuid={pipeline.id} />
                </>
              )}
            </Tab>
          ) : (
            (null as unknown as React.ReactElement)
          ) /* EDS Tabs requires ReactElement children */
        }
      </Tabs>
    </ReactEDSWrapper>
  );
};

export default PipelineDetail;
